import React, { useEffect, useState } from "react";
import InfoBox from "common-ui-components/components/info-box/InfoBox";
import StatTiles from "common-ui-components/components/stat-tiles/StatTiles";
import API from "common-ui-components/components/utils/API";
import styles from "./OverviewCards.module.css";
import InfoBoxRedirectButton from "app/common/InfoBoxRedirectButton";

const SKELETON_ROW_COUNT = 3;

const SoftwareTypeSummary = ({
  typeSummary = {},
  onFilterSelect,
  onViewSoftware,
  loading=false
}) => {
  const [prohibitedSoftware, setProhibitedSoftware] = useState([]);
  const [loadingProhibited, setLoadingProhibited] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    const getProhibitedSoftware = async () => {
      try {
        setLoadingProhibited(true);
        const response = await API.post(
          "/softwares/findAllSoftwares?page=0&size=10",
          {
            columns: [],
            sort: {},
            search: [],
            filter: [{ column: "accessType", filterValue: [1] }],
          },
          { signal: controller.signal },
        );

        if (controller.signal.aborted) return;

        if (response?.status?.success) {
          setProhibitedSoftware((response?.body?.result || []).slice(0, 3));
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error("Error fetching prohibited software: ", error);
        }
      } finally {
        if (!controller.signal.aborted) setLoadingProhibited(false);
      }
    };

    getProhibitedSoftware();
    return () => controller.abort();
  }, []);

  const counts = typeSummary.counts || {};
  const total = Number(counts.Total) || 0;
  const categories = [
    {
      title: "Prohibited",
      count: Number(counts.Prohibited) || 0,
      color: "#e52b20",
      filter: "Prohibited",
    },
    {
      title: "Commercial",
      count: Number(counts.Commercial) || 0,
      color: "#0ea66a",
      filter: "Commercial",
    },
    {
      title: "Non Commercial",
      count: Number(counts["Non Commercial"]) || 0,
      color: "#3158df",
      filter: "Non Commercial",
    },
    {
      title: "Not categorised",
      count: Math.max(
        0,
        total -
          (Number(counts.Prohibited) || 0) -
          (Number(counts.Commercial) || 0) -
          (Number(counts["Non Commercial"]) || 0),
      ),
      color: "#dce2ee",
    },
  ].map((category) => ({
    ...category,
    percent: total ? (category.count / total) * 100 : 0,
  }));

  const statItems = categories.map(
    ({ title, count, percent, color, filter }) => ({
      id: title,
      label: `${title} (${percent.toFixed(1)}%)`,
      value: count.toLocaleString(),
      color: filter ? color : "#718096",
      onClick: filter ? () => onFilterSelect?.({ id: filter }) : undefined,
    }),
  );

  const renderProhibitedContent = () => {
    if (loadingProhibited) {
      return (
        <div
          className={styles.prohibitedList}
          aria-busy="true"
          aria-label="Loading prohibited software"
        >
          {Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
            <div key={i} className={styles.prohibitedSoftware}>
              <span className={`${styles.skeleton} ${styles.skeletonName}`} />
              <span className={`${styles.skeleton} ${styles.skeletonCount}`} />
            </div>
          ))}
        </div>
      );
    }

    if (prohibitedSoftware.length) {
      return (
        <div className={styles.prohibitedList}>
          {prohibitedSoftware.map((software) => {
            const deviceCount = Number(software.installed_device_count) || 0;
            return (
              <div
                key={software.softwareVersionId}
                className={styles.prohibitedSoftware}
              >
                <span className={styles.prohibitedDot} aria-hidden="true" />
                <div className={styles.prohibitedInfo}>
                  <p className={styles.prohibitedName} title={software.name}>
                    {software.name}
                  </p>
                  {software.version && (
                    <span className={styles.prohibitedVersion}>
                      v{software.version}
                    </span>
                  )}
                </div>
                <span className={styles.prohibitedCount}>
                  {deviceCount.toLocaleString()}{" "}
                  {deviceCount === 1 ? "device" : "devices"}
                </span>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className={styles.prohibitedEmpty}>
        No prohibited software detected.
      </div>
    );
  };

  return (
    <InfoBox
      header="Software Type Summary"
      subtitle={`Share of ${total.toLocaleString()} discovered titles by type`}
      badge={<InfoBoxRedirectButton onClick={onViewSoftware} />}
      badgeProps={{ className: styles.viewSoftwareBadge }}
      className={styles.softwareSummary}
    >
      <StatTiles
        items={statItems}
        columns={4}
        className={styles.softwareTypeGrid}
        loading={loading}
      />
      <div
        className={styles.softwareTypeBar}
        aria-label="Software types distribution"
      >
        {categories.map(({ title, percent, color }) => (
          <i
            key={title}
            title={`${title}: ${percent.toFixed(1)}%`}
            style={{ width: `${percent}%`, background: color }}
          />
        ))}
      </div>
      <div className={styles.softwareTypeLegend}>
        {categories.map(({ title, count, color }) => (
          <span key={title}>
            <i style={{ background: color }} />
            {title} {count.toLocaleString()}
          </span>
        ))}
      </div>
      <div className={styles.prohibitedHeader}>
        <span>Prohibited software detected</span>
        {/* <strong>{Number(counts.Prohibited || 0).toLocaleString()} titles</strong> */}
      </div>
      {renderProhibitedContent()}
    </InfoBox>
  );
};

export default SoftwareTypeSummary;
